/**
 * Phase 23 (Plan 23-03) — D-243 role-gate route wrapper.
 *
 * Sibling to PrivateRoute.tsx — PrivateRoute checks `isAuthenticated`; RequireRole
 * additionally checks that the authenticated user has a specific role.
 *
 * Usage (inside the protected outlet of the router table):
 *   {
 *     path: 'diagnostics',
 *     element: (
 *       <RequireRole role="Super Admin">
 *         <DiagnosticsPage />
 *       </RequireRole>
 *     ),
 *   }
 *
 * Role names match `AuthConstants.RoleNames.SuperAdmin = "Super Admin"` (with
 * space) verbatim. The auth-context user.roles array carries the same strings.
 */

import type { JSX, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthContext';

export interface RequireRoleProps {
  readonly role: string;
  readonly children: ReactNode;
  readonly fallbackTo?: string;
}

/** REVIEW FF-017: normalise both sides before comparison so case + space
 *  variants (`Super Admin` vs `SuperAdmin` vs `super-admin`) don't silently
 *  break the gate if the seed role name evolves. */
function normaliseRoleName(s: string): string {
  return s.toLowerCase().replace(/[\s-_]+/g, '');
}

export function RequireRole({
  role,
  children,
  fallbackTo = '/dashboard',
}: RequireRoleProps): JSX.Element {
  const { user } = useAuth();
  const target = normaliseRoleName(role);
  if (!user?.roles.some((r) => normaliseRoleName(r) === target)) {
    return <Navigate to={fallbackTo} replace />;
  }
  return <>{children}</>;
}
